import { useCallback, useEffect, useState } from 'react';
import { Button, Card, Form, Input, Spin, Tooltip, Typography } from 'antd';
import { LockOutlined, SafetyOutlined, UserOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiError, type CaptchaChallenge } from '@mall/shared';
import { useAuth } from './AuthProvider';
import { useToast } from '../../app/ToastProvider';
import { fetchCaptcha } from './api';

interface LoginFormValues {
  username: string;
  password: string;
  captcha: string;
}

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [form] = Form.useForm<LoginFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [captcha, setCaptcha] = useState<CaptchaChallenge | null>(null);
  const [captchaLoading, setCaptchaLoading] = useState(false);

  const refreshCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    try {
      const next = await fetchCaptcha();
      setCaptcha(next);
      form.setFieldValue('captcha', '');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : '验证码加载失败，请刷新';
      toast.error(message);
    } finally {
      setCaptchaLoading(false);
    }
  }, [form, toast]);

  useEffect(() => {
    void refreshCaptcha();
  }, [refreshCaptcha]);

  const onFinish = async (values: LoginFormValues) => {
    if (!captcha) {
      toast.error('验证码尚未加载，请稍候');
      return;
    }
    setSubmitting(true);
    try {
      const session = await login({
        username: values.username,
        password: values.password,
        captchaToken: captcha.captchaToken,
        captchaCode: values.captcha.trim(),
      });
      toast.success(`欢迎回来，${session.username}`);
      const target = (location.state as LocationState | null)?.from || '/';
      navigate(target, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.message || '登录失败');
      }
      // captcha is single-use; always issue a fresh one after a failed attempt.
      void refreshCaptcha();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mall-auth-page">
      <section className="mall-auth-hero">
        <div className="mall-auth-eyebrow">Merchant Console</div>
        <Typography.Title className="mall-auth-title">
          用一个后台管理商品、订单与店铺运营
        </Typography.Title>
        <Typography.Paragraph className="mall-auth-description">
          为每个商户独立隔离数据，聚焦商品上架、分类整理、订单跟进和经营概览。
        </Typography.Paragraph>
        <div className="mall-auth-points">
          <div className="mall-auth-point">
            <strong>租户隔离</strong>
            <span>登录后只看当前商户数据</span>
          </div>
          <div className="mall-auth-point">
            <strong>商品运营</strong>
            <span>维护商品、分类和品牌</span>
          </div>
          <div className="mall-auth-point">
            <strong>订单跟踪</strong>
            <span>快速查看订单状态和统计</span>
          </div>
        </div>
      </section>
      <Card className="mall-auth-card">
        <Typography.Title level={3} style={{ marginTop: 0, marginBottom: 6 }}>
          商户登录
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 24 }}>
          输入租户子域名前缀和密码进入管理后台。
        </Typography.Paragraph>
        <Form<LoginFormValues>
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
          initialValues={{ username: '', password: '', captcha: '' }}
        >
          <Form.Item
            label="租户 ID"
            name="username"
            tooltip="即子域名前缀，如 acme（对应 acme.shop.com）"
            rules={[{ required: true, message: '请输入租户 ID' }]}
          >
            <Input
              prefix={<UserOutlined />}
              size="large"
              placeholder="如 acme（即子域名前缀）"
              autoFocus
            />
          </Form.Item>
          <Form.Item
            label="密码"
            name="password"
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password prefix={<LockOutlined />} size="large" />
          </Form.Item>
          <Form.Item label="验证码" required style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <Form.Item
                name="captcha"
                noStyle
                rules={[
                  { required: true, message: '请输入验证码' },
                  { len: 4, message: '验证码为 4 位字符' },
                ]}
              >
                <Input
                  prefix={<SafetyOutlined />}
                  size="large"
                  placeholder="不区分大小写"
                  maxLength={4}
                  autoComplete="off"
                  style={{ flex: 1 }}
                />
              </Form.Item>
              <CaptchaPreview
                challenge={captcha}
                loading={captchaLoading}
                onRefresh={refreshCaptcha}
              />
            </div>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={submitting}
              disabled={!captcha}
            >
              登录
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}

interface CaptchaPreviewProps {
  challenge: CaptchaChallenge | null;
  loading: boolean;
  onRefresh: () => void;
}

function CaptchaPreview({ challenge, loading, onRefresh }: CaptchaPreviewProps) {
  return (
    <Tooltip title="点击刷新验证码">
      <button
        type="button"
        onClick={onRefresh}
        aria-label="刷新验证码"
        disabled={loading}
        style={{
          width: 130,
          height: 40,
          padding: 0,
          border: '1px solid var(--mall-color-border)',
          borderRadius: 6,
          background: '#f5f6f8',
          cursor: loading ? 'wait' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          flex: '0 0 130px',
        }}
      >
        {loading || !challenge ? (
          <Spin size="small" />
        ) : (
          <img
            src={challenge.image}
            alt="验证码"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        )}
      </button>
    </Tooltip>
  );
}
